<template>
  <b-modal
    :id="`UploadLogoModal-${logoType}-${GET_CURRENT_SELECTED_CREATIVE.id}`"
    ref="upload-logo-modal"
    :title="title"
    size="lg"
    hide-header-close
    @hide="hideModal()"
    centered
  >
    <div class="card card-aside py-3 px-5 mb-0">
      <span
        class="card-aside-column d-flex"
        :style="`background-image: url(${previewImage});background-size: contain;`"
      >
        <div
          v-if="!previewImage"
          class="text-muted m-auto"
        >No logo set</div>
      </span>
      <div class="card-body d-flex flex-column">
        <div class="form-group">
          <div class="form-label">
            File upload or url
          </div>
          <BaseImageInput
            :value="newLogoFile"
            ref="imageInput"
            @input="setNewLogoFile($event)"
          />
          <small>or</small>
          <b-form-input
            class="form-control"
            type="url"
            placeholder="Image url"
            pattern="https?://.+"
            :value="newLogoUrl"
            @input="setNewLogoUrl($event)"
            trim
            @keyup.enter.native="applyChanges"
            :state="isValidURLUpload"
          />
        </div>
        <small class="text-muted">
          *Supported image types are jpg, jpeg, png
        </small>
        <small class="text-muted">
          Maximum image size 1MB. Images larger then 140x100px
          will be scaled to width and cropped from the center.
        </small>
      </div>
    </div>
    <template v-slot:modal-footer>
      <b-button
        variant="tertiary"
        @click="hideModal()"
        data-test="creative-properties-footer-cancel"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        @click="applyChanges()"
        data-test="uplaod-logo-modal-footer-apply"
        :disabled="!isValidUpload"
        :class="{ 'btn-loading': isUploadButtonLoading }"
      >
        Upload
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';

import urlHelper from '@/core/mixins/urlHelper';

export default {
  name: 'UploadLogoModal',
  mixins: [
    urlHelper,
  ],
  props: {
    title: {
      type: String,
      default: 'Logo Upload',
    },
    logoType: {
      type: String,
      default: 'unit',
    },
    logoUrl: {
      type: [String, null],
      default: null,
    },
  },
  data() {
    return {
      newLogoFile: null,
      newLogoUrl: '',
      isUploadButtonLoading: false,
      isValidUpload: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
      'campaign',
    ]),
    ...mapState('content', [
      'selectedCreativeId',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CURRENT_SELECTED_CREATIVE,
    ]),
    previewImage() {
      if (this.newLogoFile) return this.newLogoFile.previewURL;
      if (this.newLogoUrl) return this.newLogoUrl;
      return this.logoUrl ?? '';
    },
    supportedImageTypes() {
      return ['jpg', 'jpeg', 'png'];
    },
    isValidURLUpload() {
      if (this.newLogoUrl === '') return null;
      return this.isValidUpload;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.PATCH_LOGO_IMAGE,
      contentTypes.TRIGGER_CREATIVE_RERENDER,
    ]),
    setNewLogoFile(fileData) {
      this.resetNewLogoUrl();
      this.newLogoFile = fileData;
      this.isValidUpload = !!fileData?.previewURL;
    },
    resetNewLogoFile(resetImageInput = true) {
      this.newLogoFile = null;
      if (resetImageInput) this.$refs.imageInput.reset();
    },
    setNewLogoUrl(url) {
      this.resetNewLogoFile();
      this.newLogoUrl = url;
      this.isValidUpload = this.isValidImageURL(url, this.supportedImageTypes);
    },
    resetNewLogoUrl() {
      this.newLogoUrl = '';
    },
    hideModal() {
      this.$emit('closeModal');
    },
    applyChanges() {
      if (!this.isValidUpload) return;
      this.isUploadButtonLoading = true;
      const { orderId, id } = this.GET_CURRENT_SELECTED_CREATIVE;
      const payload = {
        advertisersId: this.selectedAdvertiser.id,
        campaignId: this.campaign.id,
        orderId,
        creativeId: id,
        type: this.logoType,
      };
      if (this.newLogoUrl) payload.logoURL = this.newLogoUrl;
      if (this.newLogoFile) payload.logoImage = this.newLogoFile;
      this.PATCH_LOGO_IMAGE(payload)
        .then(() => {
          this.resetNewLogoFile();
          this.resetNewLogoUrl();
        }).finally(() => {
          this.hideModal();
          this.isUploadButtonLoading = false;
          this.TRIGGER_CREATIVE_RERENDER();
        });
    },
  },
  created() {
    this.$root.$on('bv::modal::shown', async () => {
      this.resetNewLogoFile(false);
      this.resetNewLogoUrl();
    });
    this.$root.$on('bv::modal::hide', (bvEvent) => {
      this.hideModal();
      if (bvEvent.trigger === 'backdrop') { this.newLogoUrl = ''; this.newLogoFile = null; }
    });
  },
  destroyed() {
    this.$root.$off('bv::modal::shown');
    this.$root.$off('bv::modal::hide');
  },
};
</script>
